import { createRouter, createWebHistory } from "vue-router";
import { useStore } from "vuex";
import { getDeniedPermissions } from "../helpers/utils.js";
import {
  canAccessProviderRoute,
  hasAnyAllowedAccessBucket,
  resolveProviderAccessRedirect,
} from "../helpers/providerAccess.js";

const Home = () => import("../views/Home.vue");
const BlogIndexView = () => import("../views/BlogIndexView.vue");
const BlogPostView = () => import("../views/BlogPostView.vue");
const Client_Home = () => import("../views/Client_Home.vue");
const Client_Order = () => import("../views/Client_Order.vue");
const Client_New_Order = () => import("../views/Client_New_Order.vue");
const Provider_Home = () => import("../views/Provider_Home.vue");
const Provider_Inventory = () => import("../views/Provider_Inventory.vue");
const Provider_Locations = () => import("../views/Provider_Locations.vue");
const Provider_Location = () => import("../views/Provider_Location.vue");
const Provider_Products = () => import("../views/Provider_Products.vue");
const Provider_Product_Bundles = () =>
  import("../views/Provider_Product_Bundles.vue");
const Provider_Product_Bundle = () =>
  import("../views/Provider_Product_Bundle.vue");
const Provider_Product = () => import("../views/Provider_Product.vue");
const Provider_Orders = () => import("../views/Provider_Orders.vue");
const Provider_Clients = () => import("../views/Provider_Clients.vue");
const Provider_Client = () => import("../views/Provider_Client.vue");
const Provider_Client_Orders = () =>
  import("../views/Provider_Client_Orders.vue");
const Provider_Order = () => import("../views/Provider_Order.vue");
const Provider_Order_Print = () => import("../views/Provider_Order_Print.vue");
const Provider_Transactions = () => import("../views/Provider_Transactions.vue");
const Provider_New_Order = () => import("../views/Provider_New_Order.vue");
const Provider_Deliveries = () => import("../views/Provider_Deliveries.vue");
const Provider_Delivery = () => import("../views/Provider_Delivery.vue");
const Provider_Metadata = () => import("../views/Provider_Metadata.vue");
const Provider_Notifications = () => import("../views/Provider_Notifications.vue");
const Provider_Notification_Events = () =>
  import("../views/Provider_Notification_Events.vue");
const Provider_Settings = () => import("../views/Provider_Settings.vue");
const InviteView = () => import("../views/InviteView.vue");
const Provider_Users = () => import("../views/Provider_Users.vue");
const LoginView = () => import("../views/LoginView.vue");
const SignupView = () => import("../views/SignupView.vue");
const OtpView = () => import("../views/OtpView.vue");
const ResetView = () => import("../views/ResetView.vue");
const AuthContinueView = () => import("../views/AuthContinueView.vue");

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/blog",
    name: "Blog_Index",
    component: BlogIndexView,
    meta: {
      public: true,
    },
  },
  {
    path: "/blog/:slug",
    name: "Blog_Post",
    component: BlogPostView,
    meta: {
      public: true,
    },
  },
  {
    path: "/client/:client_id",
    name: "Client_Home",
    component: Client_Home,
  },
  {
    path: "/client/:client_id/order/:order_id",
    name: "Client_Order",
    component: Client_Order,
  },
  {
    path: "/client/:client_id/order",
    name: "Client_New_Order",
    component: Client_New_Order,
  },
  {
    path: "/provider/:provider_id/adjust",
    name: "Provider_Adjust",
    component: Provider_Inventory,
  },
  {
    path: "/provider/:provider_id/inbox",
    name: "Provider_Home",
    component: Provider_Home,
  },
  {
    path: "/provider/:provider_id/inventory",
    name: "Provider_Inventory",
    component: Provider_Inventory,
  },
  {
    path: "/provider/:provider_id/orders",
    name: "Provider_Orders",
    component: Provider_Orders,
  },
  {
    path: "/provider/:provider_id/clients",
    name: "Provider_Clients",
    component: Provider_Clients,
  },
  {
    path: "/provider/:provider_id/client/:client_id",
    name: "Provider_Client",
    component: Provider_Client,
  },
  {
    path: "/provider/:provider_id/client/:client_id/orders",
    name: "Provider_Client_Orders",
    component: Provider_Client_Orders,
  },
  {
    path: "/provider/:provider_id/locations",
    name: "Provider_Locations",
    component: Provider_Locations,
  },
  {
    path: "/provider/:provider_id/location/:location_id",
    name: "Provider_Location",
    component: Provider_Location,
  },
  {
    path: "/provider/:provider_id/products",
    name: "Provider_Products",
    component: Provider_Products,
  },
  {
    path: "/provider/:provider_id/product-bundles",
    name: "Provider_Product_Bundles",
    component: Provider_Product_Bundles,
  },
  {
    path: "/provider/:provider_id/product-bundles/:bundle_id",
    name: "Provider_Product_Bundle",
    component: Provider_Product_Bundle,
  },
  {
    path: "/provider/:provider_id/product/:product_id",
    name: "Provider_Product",
    component: Provider_Product,
  },
  {
    path: "/provider/:provider_id/order/:order_id",
    name: "Provider_Order",
    component: Provider_Order,
  },
  {
    path: "/provider/:provider_id/deliveries",
    name: "Provider_Deliveries",
    component: Provider_Deliveries,
  },
  {
    path: "/provider/:provider_id/delivery/:delivery_id",
    name: "Provider_Delivery",
    component: Provider_Delivery,
  },
  {
    path: "/provider/:provider_id/print/:order_id",
    name: "Provider_Order_Print",
    component: Provider_Order_Print,
  },
  {
    path: "/provider/:provider_id/transactions",
    name: "Provider_Transactions",
    component: Provider_Transactions,
  },
  {
    path: "/provider/:provider_id/transaction/:order_id",
    name: "Provider_Transaction",
    component: Provider_Order,
  },
  {
    path: "/provider/:provider_id/order",
    name: "Provider_New_Order",
    component: Provider_New_Order,
  },
  {
    path: "/provider/:provider_id/transaction",
    name: "Provider_New_Transaction",
    component: Provider_New_Order,
  },
  {
    path: "/provider/:provider_id/transaction/in",
    name: "Provider_Inbound",
    redirect: (to) => `/provider/${to.params.provider_id}/transaction`,
  },
  {
    path: "/provider/:provider_id/transaction/out",
    name: "Provider_Outbound",
    redirect: (to) => `/provider/${to.params.provider_id}/transaction`,
  },
  {
    path: "/provider/:provider_id/metadata",
    name: "Provider_Metadata",
    component: Provider_Metadata,
  },
  {
    path: "/provider/:provider_id/users",
    name: "Provider_Users",
    component: Provider_Users,
  },
  {
    path: "/provider/:provider_id/notifications",
    name: "Provider_Notifications",
    component: Provider_Notifications,
  },
  {
    path: "/provider/:provider_id/notifications/events",
    name: "Provider_Notification_Events",
    component: Provider_Notification_Events,
  },
  {
    path: "/provider/:provider_id/settings",
    name: "Provider_Settings",
    component: Provider_Settings,
  },
  {
    path: "/login",
    name: "Login",
    component: LoginView,
  },
  {
    path: "/signup",
    name: "Signup",
    component: SignupView,
  },
  {
    path: "/otp",
    name: "OTP Login",
    component: OtpView,
  },
  {
    path: "/reset",
    name: "Reset Password",
    component: ResetView,
  },
  {
    path: "/auth/recovery/:tokenHash",
    name: "Auth Continue Recovery",
    component: AuthContinueView,
    props: {
      confirmationType: "recovery",
    },
  },
  {
    path: "/auth/invite/:tokenHash",
    name: "Auth Continue Invite",
    component: AuthContinueView,
    props: {
      confirmationType: "invite",
    },
  },
  {
    path: "/invite",
    name: "Invite",
    component: InviteView,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

function redirectToLogin(store) {
  if (store) {
    store.commit("setSession", null);
  }

  if (typeof window !== "undefined") {
    try {
      localStorage.setItem("enlazado_session", "null");
    } catch (error) {
      console.log("Failed to clear persisted session", error);
    }
  }

  return { name: "Login" };
}

router.beforeEach((to) => {
  if (!to.name) {
    return true;
  }

  if (
    to.meta?.public === true ||
    to.name == "Home" ||
    to.name == "Login" ||
    to.name == "Signup" ||
    to.name == "OTP Login" ||
    to.name == "Reset Password" ||
    to.name == "Auth Continue Recovery" ||
    to.name == "Auth Continue Invite" ||
    to.name == "Invite"
  ) {
    return true;
  }

  if (to.name.startsWith("Client_")) {
    return true;
  }

  const store = useStore();

  if (
    (!store.state.session ||
      !Array.isArray(store.state.session.providers) ||
      store.state.session.providers.length === 0) &&
    typeof window !== "undefined"
  ) {
    try {
      const persisted = localStorage.getItem("enlazado_session");
      if (persisted) {
        const parsed = JSON.parse(persisted);
        if (
          parsed &&
          Array.isArray(parsed.providers) &&
          parsed.providers.length > 0
        ) {
          store.commit("setSession", parsed);
        }
      }
    } catch (error) {
      console.log("Failed to restore persisted session", error);
    }
  }

  if (
    !store.state.session ||
    !Array.isArray(store.state.session.providers) ||
    store.state.session.providers.length === 0
  ) {
    return redirectToLogin(store);
  }

  const ids = store.state.session.providers
    .map((p) => {
      return p?.provider?.id ?? p?.provider_id ?? p?.id ?? null;
    })
    .filter((id) => id !== null && id !== undefined)
    .map((id) => String(id));

  if (!to.params.provider_id || !ids.includes(String(to.params.provider_id))) {
    return redirectToLogin(store);
  }

  const deniedPermissions = getDeniedPermissions(
    store.state.session,
    to.params.provider_id
  );

  if (!hasAnyAllowedAccessBucket(deniedPermissions)) {
    return redirectToLogin(store);
  }

  if (!canAccessProviderRoute(to.name, deniedPermissions)) {
    const redirectTarget = resolveProviderAccessRedirect(
      to.params.provider_id,
      deniedPermissions
    );

    if (!redirectTarget || redirectTarget.name === to.name) {
      return redirectToLogin(store);
    }

    return redirectTarget;
  }

  return true;
});

export default router;
